import type { ApiResponse } from "@/types/common";
import { apiClient, unwrapApiResponse } from "./httpClient";

export interface ChannelStockAllocation {
  partyId: number;
  businessName: string;
  levelText: string;
  quantity: number;
}

export interface ChannelStockMovement {
  id: number;
  partyId?: number;
  holderName: string;
  quantityDelta: number;
  balanceAfter: number;
  movementTypeText: string;
  counterpartyName?: string;
  note?: string;
  createdAt: string;
}

export interface ChannelStockProduct {
  productId: number;
  productName: string;
  storeStock: number;
  adminQuantity: number;
  partiesWithStock: number;
  allocations: ChannelStockAllocation[];
  movements: ChannelStockMovement[];
}

export interface ChannelStockAdminItem {
  productId: number;
  productName: string;
  sku?: string;
  storeName?: string;
  adminId?: string;
  customerPrice: number;
  adminQuantity: number;
  distributorCommissionPercent: number;
  wholesaleCommissionPercent: number;
  retailCommissionPercent: number;
  isPublished: boolean;
}

export interface ChannelStockMine {
  productId: number;
  productName: string;
  sku?: string;
  barcode?: string;
  imageUrl?: string;
  categoryId?: number;
  categoryName?: string;
  price: number;
  sellingPrice: number;
  mrp: number;
  distributorPrice: number;
  wholesalePrice: number;
  retailPrice: number;
  businessVolume: number;
  quantity: number;
  availableQuantity: number;
  stockQuantity: number;
  lowStockThreshold: number;
  status: string;
  isActive: boolean;
  unit: string;
  unitValue: number;
}

export const channelStockService = {
  getProduct(productId: number): Promise<ChannelStockProduct> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<ChannelStockProduct>>(`/ChannelStock/product/${productId}`),
    );
  },

  setAdmin(productId: number, quantity: number, note?: string): Promise<ChannelStockProduct> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<ChannelStockProduct>>("/ChannelStock/admin", { productId, quantity, note }),
    );
  },

  listAdmin(): Promise<ChannelStockAdminItem[]> {
    return unwrapApiResponse(apiClient.get<ApiResponse<ChannelStockAdminItem[]>>("/ChannelStock/admin"));
  },

  getMine(params?: { search?: string; category?: string; stockStatus?: string }): Promise<ChannelStockMine[]> {
    const query = new URLSearchParams();
    if (params?.search) query.set("search", params.search);
    if (params?.category && params.category !== "all") query.set("category", params.category);
    if (params?.stockStatus && params.stockStatus !== "all") query.set("stockStatus", params.stockStatus);
    const qs = query.toString();
    return unwrapApiResponse(apiClient.get<ApiResponse<ChannelStockMine[]>>(`/ChannelStock/me${qs ? `?${qs}` : ""}`));
  },
};
